import { ComponentType, lazy, LazyExoticComponent, Suspense } from 'react';
import { createBrowserRouter } from 'react-router-dom';
import { environment } from 'env/cashier';

import { initiateQAToolkit } from '@cashier/cashier-common';
import {
  fetchCertainPaymentMethod,
  initialiseCashierConfiguration,
  onTranslationsReady,
} from '@cashier/cashier-data-access';
import {
  CashierConfiguration,
  PaymentTypes,
  SubmitInitiators,
  Versions,
} from '@cashier/cashier-model';
import {
  ConfigurationExtractor,
  convertToLocale,
  initI18n,
} from '@cashier/cashier-shared';

const MainPageV2 = lazy(() => import('../pages/main/v2/main'));
const MainPageV4 = lazy(() => import('../pages/main/v4/main'));
const LoadingPage = lazy(() => import('../pages/loading/loading'));
const ConfirmationPage = lazy(
  () => import('../pages/confirmation/confirmation'),
);
const wrapperWithSuspense = (Component: LazyExoticComponent<ComponentType>) => (
  <Suspense fallback={null}>
    <Component />
  </Suspense>
);

interface AppRouterCallbacks {
  setClientVersion: (version: string) => void;
}

export const getAppRouter = (
  store,
  { setClientVersion }: AppRouterCallbacks,
) => {
  initiateQAToolkit();

  const loadTranslations = (
    merchantId: string,
    sessionId: string,
    userId: string,
    country: string,
    language: string,
  ) => {
    const onLoaded = () => {
      console.log('translations ready'); // eslint-disable-line no-console

      store.dispatch(onTranslationsReady());
    };

    initI18n(
      {
        url: environment.paymentGatewayApiUrl,
        context: {
          merchantId,
          sessionId,
          userId,
          country,
          acceptLanguage: convertToLocale(language || 'en'),
        },
      },
      onLoaded,
    );
  };

  const loadPaymentMethod = (
    methodId: string,
    paymentType: PaymentTypes,
    bonus: string,
  ) => {
    store.dispatch(
      fetchCertainPaymentMethod({
        type: paymentType,
        id: methodId,
        bonus,
      }),
    );
  };

  const setupCashierConfiguration = (configuration: CashierConfiguration) => {
    store.dispatch(initialiseCashierConfiguration(configuration));
  };

  return createBrowserRouter([
    {
      path: '/:theme/:paymentMethodId',
      element: wrapperWithSuspense(MainPageV2),
      loader: ({ params }) => {
        const configuration = ConfigurationExtractor.fromRequestUri();
        const {
          submitInitiator,
          paymentType,
          bonus,
          merchantId,
          sessionId,
          userId,
          country,
          language,
        } = configuration;

        const version =
          submitInitiator === SubmitInitiators.Parent
            ? Versions.v3
            : Versions.v2;

        paymentType &&
          loadPaymentMethod(params.paymentMethodId, paymentType, bonus);

        loadTranslations(merchantId, sessionId, userId, country, language);
        setupCashierConfiguration({
          ...configuration,
          version,
          notifyCustomerAboutPaymentResult: false,
          cashierFieldUrl: environment.cashierFieldHost,
        });
        setClientVersion(version);

        return null;
      },
    },
    {
      path: '/v2/:paymentMethodId',
      element: wrapperWithSuspense(MainPageV2),
      loader: ({ params }) => {
        const version = Versions.v2;
        const configuration = ConfigurationExtractor.fromRequestUri();

        const {
          paymentType,
          bonus,
          merchantId,
          sessionId,
          userId,
          country,
          language,
        } = configuration;

        paymentType &&
          loadPaymentMethod(params.paymentMethodId, paymentType, bonus);

        loadTranslations(merchantId, sessionId, userId, country, language);
        setupCashierConfiguration({
          ...configuration,
          version,
          notifyCustomerAboutPaymentResult: false,
          cashierFieldUrl: environment.cashierFieldHost,
        });

        setClientVersion(version);
        return null;
      },
    },
    {
      path: '/v4/:paymentMethodId',
      element: wrapperWithSuspense(MainPageV4),
      loader: ({ params }) => {
        const version = Versions.v4;
        const configuration = ConfigurationExtractor.fromRequestUri();

        const {
          paymentType,
          bonus,
          merchantId,
          sessionId,
          userId,
          country,
          language,
        } = configuration;

        paymentType &&
          loadPaymentMethod(params.paymentMethodId, paymentType, bonus);
        loadTranslations(merchantId, sessionId, userId, country, language);
        setupCashierConfiguration({
          ...configuration,
          notifyCustomerAboutPaymentResult: true,
          version,
          cashierFieldUrl: environment.cashierFieldHost,
        });

        setClientVersion(version);
        return null;
      },
    },
    {
      path: '/confirm/:id',
      element: wrapperWithSuspense(ConfirmationPage),
      loader: () => {
        const { merchantId, sessionId, userId, country, language } =
          ConfigurationExtractor.fromRequestUri();

        loadTranslations(merchantId, sessionId, userId, country, language);
        return null;
      },
    },
    {
      path: '/confirm',
      element: wrapperWithSuspense(ConfirmationPage),
      loader: () => {
        const { merchantId, sessionId, userId, country, language } =
          ConfigurationExtractor.fromRequestUri();

        loadTranslations(merchantId, sessionId, userId, country, language);
        return null;
      },
    },
    {
      path: '/success',
      element: wrapperWithSuspense(ConfirmationPage),
      loader: () => {
        const { merchantId, sessionId, userId, country, language } =
          ConfigurationExtractor.fromRequestUri();

        loadTranslations(merchantId, sessionId, userId, country, language);
        return null;
      },
    },
    {
      path: '/failure',
      element: wrapperWithSuspense(ConfirmationPage),
      loader: () => {
        const { merchantId, sessionId, userId, country, language } =
          ConfigurationExtractor.fromRequestUri();

        loadTranslations(merchantId, sessionId, userId, country, language);
        return null;
      },
    },
    {
      path: '/status/:merchantId/:id/:paymentType',
      element: wrapperWithSuspense(ConfirmationPage),
      loader: () => {
        const { merchantId, sessionId, userId, country, language } =
          ConfigurationExtractor.fromRequestUri();

        loadTranslations(merchantId, sessionId, userId, country, language);
        return null;
      },
    },
    {
      path: '/loading',
      element: wrapperWithSuspense(LoadingPage),
      loader: () => {
        const { merchantId, sessionId, userId, country, language } =
          ConfigurationExtractor.fromRequestUri();

        loadTranslations(merchantId, sessionId, userId, country, language);
        return null;
      },
    },
  ]);
};
